import React, { useState } from 'react';
import './Sage50ExportButton.css';
import ApiRequest from '../../../actions/ApiRequest';
import moment from 'moment';

const Sage50ExportButton = ({ reportData, filterParams }) => {
    const [exporting, setExporting] = useState(false);
    const [result, setResult] = useState(null);

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0]; // YYYY-MM-DD
    };

    const exportToCSV = (data, filename) => {
        // Create CSV content
        const headers = ['Date', 'Invoice Number', 'Customer Ref', 'Description', 'Quantity', 'Amount', 'Tax Code'];
        const csvRows = [headers.join(',')];

        data.forEach(sale => {
            const row = [
                sale.date,
                sale.invoice_number,
                sale.customer_ref,
                `"${sale.description.replace(/"/g, '""')}"`, // Escape quotes in description
                sale.quantity,
                sale.amount.toFixed(2),
                sale.tax_code
            ];
            csvRows.push(row.join(','));
        });

        const csvContent = csvRows.join('\n');
        
        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExport = async () => {
        setExporting(true);
        setResult(null);

        try {
            let allSalesData = [];

            // If filterParams are provided, fetch ALL data for the date range
            if (filterParams) {
                console.log('Sage 50 Export: Fetching ALL sales data for date range...');
                
                const from_date = moment(filterParams.dateFrom).format("YYYY-MM-DD");
                const to_date = moment(filterParams.dateTo).format("YYYY-MM-DD");
                const phone = filterParams.phone || "";
                const transType = filterParams.transType || "";
                const transStatus = filterParams.transStatus || "";
                const transId = filterParams.transId || "";
                const cardId = filterParams.cardId || "";
                const cancelRequest = filterParams.cancelRequest || "";
                const canceled = filterParams.canceled || "";
                const amount = filterParams.amount || "";
                const renew = filterParams.renew || "";
                const provider = filterParams.provider || "";
                const cart_type = filterParams.cardType || "";
                const seller_id = filterParams.seller;
                const resellerId = filterParams.resellerId || "";
                const topUpAccountNo = filterParams.topUpAccountNo || "";
                const location = filterParams.location || "";
                const reportType = "sales"; // ensure we're getting sales
                const note = filterParams.note || "";
                const hide = false;
                
                // Fetch data with a large limit to get all records
                const response = await ApiRequest.post(
                    `get_seller_report?location=${location}&report_type=$${reportType}&from_date=${from_date}&to_date=${to_date}&cancelRequest=${cancelRequest}&seller_id=${seller_id}&cardId=${cardId}&amount=${amount}&canceled=${canceled}&trans_id=${transId}&autorenew=${renew}&trans_status=${transStatus}&trans_type=${transType}&number=${phone}&provider=${provider}&cart_type=${cart_type}&offset=0&no_of_rows=999999&topup_account=${topUpAccountNo}&reseller_id=${resellerId}&hide=${hide}&note=${note}`
                );

                // The response should contain the data
                if (response && response.data && response.data.data) {
                    allSalesData = response.data.data;
                } else {
                    // Fallback to current page data if fetch fails
                    allSalesData = reportData || [];
                }
            } else {
                // No filter params, use current page data
                allSalesData = reportData || [];
            }

            if (!allSalesData || allSalesData.length === 0) {
                alert('⚠️ No sales data available to export');
                setExporting(false);
                return;
            }

            console.log(`Sage 50 Export: Exporting ${allSalesData.length} sales transactions`);
            
            // Format sales data for Sage 50
            const salesData = allSalesData.map(sale => ({
                date: formatDate(sale.date || sale.datetime),
                invoice_number: sale.transid || sale.transaction_id || sale.id,
                customer_ref: sale.customer_id || 'CASH',
                description: sale.description || sale.en_des || sale.ar_des || sale.he_des || `Sale ${sale.id}`,
                quantity: sale.quantity || 1,
                amount: parseFloat(sale.cardamount || sale.amount || 0),
                tax_code: 1
            }));

            // Generate filename with date range
            const dateStr = formatDate(new Date()).replace(/-/g, '');
            const filename = `Sage50_Sales_Export_${dateStr}.csv`;

            // Export to CSV
            exportToCSV(salesData, filename);

            setResult({
                type: 'success',
                message: `✅ Successfully exported ${salesData.length} sales transactions to ${filename}`
            });

            // Auto-hide success message after 5 seconds
            setTimeout(() => setResult(null), 5000);

        } catch (error) {
            console.error('Sage 50 Export Error:', error);
            setResult({
                type: 'error',
                message: `❌ Export failed: ${error.message}`
            });
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <button
                className="sage50-export-btn online"
                onClick={handleExport}
                disabled={exporting}
                title="Export Sales to Sage 50 CSV"
                style={{ margin: '0 4px' }}
            >
                {exporting ? (
                    <span className="spinner"></span>
                ) : (
                    <span className="sage50-text">Sage 50</span>
                )}
            </button>

            {result && (
                <div className={`sage50-result ${result.type}`}>
                    <div className="result-message">{result.message}</div>
                </div>
            )}
        </>
    );
};

export default Sage50ExportButton;

